import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import conhecaBadge from "../../../assets/images/conheca-badge.png";
import { getImageUrl } from "../../../utils/media";
import "./style.css";

const AUTOPLAY_INTERVAL = 5000;

export default function HeroBanner({
  slides: slidesProp,
  highlightPhrase,
}) {
  const slides = Array.isArray(slidesProp) ? slidesProp : [];
  const fraseTexto = "Conheça o 7 Gerações!";
  const fraseLink = highlightPhrase?.link || "/quem-somos";

  const [currentSlide, setCurrentSlide] = useState(0);
  const [failedSlides, setFailedSlides] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setFailedSlides({});
    setCurrentSlide(0);
  }, [slidesProp]);

  useEffect(() => {
    if (slides.length <= 1 || isPaused) return undefined;
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_INTERVAL);
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isPaused, slides.length]);

  const goToSlide = (index) => setCurrentSlide(index);
  const togglePlayback = () => {
    if (!isPaused && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsPaused((paused) => !paused);
  };

  return (
    <section className="hero-banner">
      <div className="hero-banner__track">
        {slides.length > 0 ? (
          slides.map((slide, index) => {
            const imageUrl = getImageUrl(
              slide?.imageUrl || slide?.url || slide,
            );
            const failureState = failedSlides[index];
            const sourceUrl = failureState === "unavailable" ? null : imageUrl;

            return (
              <div
                key={slide._id || index}
                className={`hero-banner__slide ${index === currentSlide ? "hero-banner__slide--active" : ""}`}
                aria-hidden={index !== currentSlide}
              >
                {sourceUrl ? (
                  <img
                    className="hero-banner__slide-image"
                    src={sourceUrl}
                    alt=""
                    aria-hidden="true"
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    style={slide.focalPoint ? { objectPosition: slide.focalPoint } : {}}
                    onError={() => {
                      setFailedSlides((current) => ({
                        ...current,
                        [index]: "unavailable",
                      }));
                    }}
                  />
                ) : (
                  <div className="hero-banner__placeholder" />
                )}
              </div>
            );
          })
        ) : (
          <div className="hero-banner__slide hero-banner__slide--active">
            <div className="hero-banner__placeholder" />
          </div>
        )}
      </div>

      <button
        type="button"
        className="hero-banner__play-pause"
        onClick={togglePlayback}
        aria-label={isPaused ? "Reproduzir slideshow" : "Pausar slideshow"}
        aria-pressed={isPaused}
      >
        <svg
          width="45"
          height="45"
          viewBox="0 0 45 45"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="0.5" y="0.5" width="44" height="44" rx="22" fill="white" />
          <rect
            x="0.5"
            y="0.5"
            width="44"
            height="44"
            rx="22"
            stroke="#234E9E"
          />
          <path
            d={isPaused
              ? "M17 13.5L32 22.5L17 31.5V13.5Z"
              : "M17.1667 11.5H15.3333C14.8471 11.5 14.3808 11.6932 14.037 12.037C13.6932 12.3808 13.5 12.8471 13.5 13.3333V31.6667C13.5 32.1529 13.6932 32.6192 14.037 32.963C14.3808 33.3068 14.8471 33.5 15.3333 33.5H17.1667C17.6529 33.5 18.1192 33.3068 18.463 32.963C18.8068 32.6192 19 32.1529 19 31.6667V13.3333C19 12.8471 18.8068 12.3808 18.463 12.037C18.1192 11.6932 17.6529 11.5 17.1667 11.5ZM29.0833 11.5H27.25C26.7638 11.5 26.2975 11.6932 25.9536 12.037C25.6098 12.3808 25.4167 12.8471 25.4167 13.3333V31.6667C25.4167 32.1529 25.6098 32.6192 25.9536 32.963C26.2975 33.3068 26.7638 33.5 27.25 33.5H29.0833C29.5696 33.5 30.0359 33.3068 30.3797 32.963C30.7235 32.6192 30.9167 32.1529 30.9167 31.6667V13.3333C30.9167 12.8471 30.7235 12.3808 30.3797 12.037C30.0359 11.6932 29.5696 11.5 29.0833 11.5Z"}
            stroke="#234E9E"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Link
        to={fraseLink}
        className="hero-banner__conheca"
        aria-label={fraseTexto}
      >
        <img
          src={conhecaBadge}
          alt={fraseTexto}
          className="hero-banner__conheca-img"
        />
      </Link>

      {slides.length > 1 && (
        <div className="hero-banner__dots">
          {slides.map((slide, index) => (
            <button
              key={slide._id || index}
              type="button"
              className={`hero-banner__dot ${index === currentSlide ? "hero-banner__dot--active" : ""}`}
              onClick={() => goToSlide(index)}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
